import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { Building2, Loader2, Clock, CheckCircle2, XCircle } from 'lucide-react'

const STATUS_CONFIG: Record<string, { label: string; icon: any; className: string }> = {
  pending: { label: 'Pending review', icon: Clock, className: 'bg-[hsl(var(--warning)/0.18)] text-warning' },
  approved: { label: 'Approved', icon: CheckCircle2, className: 'bg-[hsl(var(--success)/0.15)] text-success' },
  rejected: { label: 'Rejected', icon: XCircle, className: 'bg-[hsl(var(--destructive)/0.12)] text-destructive' },
}

export function PortalOutletChangeCard() {
  const { toast } = useToast()
  const qc = useQueryClient()
  const [newOutletId, setNewOutletId] = useState('')
  const [reason, setReason] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const { data: crm } = useQuery({
    queryKey: ['portal-outlet-crm'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user?.email) return null
      const { data } = await (supabase
        .from('journalists' as any)
        .select('id, org_id, outlet_id, media_outlets(name)')
        .eq('email', user.email)
        .maybeSingle() as any)
      return data as any | null
    },
  })

  const { data: outlets } = useQuery({
    queryKey: ['portal-outlet-options', crm?.org_id],
    enabled: !!crm?.org_id,
    queryFn: async () => {
      const { data } = await (supabase
        .from('media_outlets' as any)
        .select('id, name')
        .eq('org_id', crm.org_id)
        .order('name') as any)
      return (data ?? []) as { id: string; name: string }[]
    },
  })

  const { data: requests } = useQuery({
    queryKey: ['portal-outlet-requests', crm?.id],
    enabled: !!crm?.id,
    queryFn: async () => {
      const { data } = await (supabase
        .from('outlet_change_requests' as any)
        .select('id, status, reason, reviewer_notes, created_at, reviewed_at, media_outlets:requested_outlet_id(name)')
        .eq('journalist_id', crm.id)
        .order('created_at', { ascending: false }) as any)
      return (data ?? []) as any[]
    },
  })

  const pendingRequest = (requests ?? []).find((r) => r.status === 'pending')

  async function submitRequest() {
    if (!crm || !newOutletId) return
    setSubmitting(true)
    try {
      const { data: { user } } = await supabase.auth.getUser()
      const { error } = await supabase.from('outlet_change_requests' as any).insert({
        org_id: crm.org_id,
        journalist_id: crm.id,
        current_outlet_id: crm.outlet_id,
        requested_outlet_id: newOutletId,
        reason: reason.trim() || null,
        requested_by: user?.id ?? null,
      })
      if (error) throw error
      toast({ title: 'Outlet change request submitted', description: 'The institution will review your request.' })
      setNewOutletId('')
      setReason('')
      qc.invalidateQueries({ queryKey: ['portal-outlet-requests', crm.id] })
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' })
    } finally {
      setSubmitting(false)
    }
  }

  if (!crm) return null

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Building2 className="w-4 h-4" />
          Outlet
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <p className="text-justify text-xs text-muted-foreground">Current outlet</p>
          <p className="text-justify text-sm font-medium">{crm.media_outlets?.name ?? '—'}</p>
        </div>

        {pendingRequest ? (
          <div className="rounded-lg border border-[hsl(var(--warning)/0.3)] bg-[hsl(var(--warning)/0.08)] p-3 text-sm">
            <div className="flex items-center gap-1.5 text-warning font-medium">
              <Clock className="w-3.5 h-3.5" />
              Request pending: move to {pendingRequest.media_outlets?.name}
            </div>
            <p className="text-justify text-xs text-warning/80 mt-1">
              Submitted {new Date(pendingRequest.created_at).toLocaleDateString()}. Awaiting institution approval.
            </p>
          </div>
        ) : (
          <div className="space-y-3 pt-1 border-t border-border">
            <p className="text-justify text-xs text-muted-foreground pt-3">
              Moved to a new organisation? Request an outlet change — it must be approved by the institution.
            </p>
            <div className="space-y-1.5">
              <Label className="text-xs">New outlet</Label>
              <Select value={newOutletId} onValueChange={setNewOutletId}>
                <SelectTrigger><SelectValue placeholder="Select outlet…" /></SelectTrigger>
                <SelectContent>
                  {(outlets ?? [])
                    .filter((o) => o.id !== crm.outlet_id)
                    .map((o) => (
                      <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Reason <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. I have moved from Company A to Company B"
                rows={2}
              />
            </div>
            <Button
              size="sm"
              disabled={!newOutletId || submitting}
              onClick={submitRequest}
              style={{ backgroundColor: 'hsl(var(--success))' }}
              className="text-white hover:opacity-90"
            >
              {submitting && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
              Request Outlet Change
            </Button>
          </div>
        )}

        {(requests ?? []).length > 0 && (
          <div className="pt-3 border-t border-border space-y-2">
            <p className="text-justify text-xs font-medium text-muted-foreground">History</p>
            {(requests ?? []).map((r) => {
              const sc = STATUS_CONFIG[r.status] ?? STATUS_CONFIG.pending
              const Icon = sc.icon
              return (
                <div key={r.id} className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">
                    {r.media_outlets?.name} · {new Date(r.created_at).toLocaleDateString()}
                  </span>
                  <Badge className={`text-[10px] gap-1 ${sc.className}`}>
                    <Icon className="w-3 h-3" />
                    {sc.label}
                  </Badge>
                </div>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
